import { Controller, useForm } from "react-hook-form";
import { formatErrorMessageV2, InputV2, MultiDropdownV2, OptionV2 } from "../../../../FormControlsV2";
import Constants from "../../../../../constants/Index";
import axios from "../../../../../services/axios";
import { useEffect, useMemo, useState } from "react";
import { FooterEditOnboarding } from "../../../../EditOnboarding/Footer";
import { useFilter } from "../../../../../hooks/useFilter";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Modal } from "../../../../Modal/Default/Index";
import { ButtonOnlyTitle } from "../../../../Buttons/ButtonOnlyTitle/Index";
import "./styles.css";
import { useTranslation } from "react-i18next";

function displayNumericFormValue(v: unknown): string | number {
    if (v === null || v === undefined) return "";
    if (v === " " || v === "") return "";
    if (typeof v === "number" && Number.isNaN(v)) return "";
    if (typeof v === "string" && v.trim() === "") return "";
    return v as number;
}

type WeightAnimalsProps = {
    farmSelected: OptionV2<number> | null;
};

const monthOptions: OptionV2[] = Constants.MONTHS_FULL_NAME_OPTIONS;

export function WeightAnimals({ farmSelected }: WeightAnimalsProps) {
    const { t } = useTranslation();
    const [sumProportion, setSumProportion] = useState(0);

    const schema = yup.object().shape({
        farm: yup.object().required(t("weightCategories.selectFarm")),
        months: yup.array().min(1, t("weightCategories.selectOneMonth")).required(t("weightCategories.selectOneMonth")),
        proportion: yup.array()
            .test(
                "sum-100",
                t("weightCategories.proportionsMustTotal"),
                (proportion = []) => {
                    const sum = proportion.reduce((acc: number, curr: any) => acc + (Number(curr) || 0), 0);
                    setSumProportion(Math.ceil(sum))
                    return Math.ceil(sum) === 100;
                }
            ),
        parameter: yup.object().shape({
            BIRTH_WEIGHT: yup.object().shape({
                MALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
                FEMALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
            }),
            WEANING_WEIGHT_210_DAYS: yup.object().shape({
                MALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
                FEMALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
            }),
            AVERAGE_DAILY_GAIN_REARING_WET: yup.object().shape({
                MALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
                FEMALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
            }),
            AVERAGE_DAILY_GAIN_REARING_DRY: yup.object().shape({
                MALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
                FEMALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
            }),
            ADULT_WEIGHT: yup.object().shape({
                MALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
                FEMALE: yup.number().min(0, t("weightCategories.nonNegative")).required(t("weightCategories.required")),
            }),
        })
    });

    const {
        handleSubmit,
        watch,
        setValue,
        control,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(schema),
    });
    const { setSystemManager } = useFilter();

    const [errorModal, setErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [submiting, setSubmiting] = useState(false);
    const [monthlyEvolution] = useState(new Map<number, any>());
    const [parameters] = useState(new Map<string, any>());
    const months = watch("months");
    useEffect(() => {
        setValue("farm", farmSelected);
    }, [farmSelected, setValue]);

    const monthSelectOptions: OptionV2[] = useMemo(() => {
        if (months && months.length === 12) return [];
        if (!months || months.length === 0) {
            return monthOptions;
        }
        const lastMonthValue = Number(months[months.length - 1].value);
        if (lastMonthValue === Constants.MONTHS_FULL_NAME_OPTIONS[Constants.MONTHS_FULL_NAME_OPTIONS.length - 1]?.value) {
            return monthOptions.filter(
                (option) => option?.value === Constants.MONTHS_FULL_NAME_OPTIONS[0]?.value,
            );
        }
        return monthOptions.filter(
            (option) =>
                option?.value === lastMonthValue + 1 &&
                !months.some((m: OptionV2) => Number(m?.value) === option?.value),
        );
    }, [months]);

    useEffect(() => {
        Constants.MONTHS_FULL_NAME_OPTIONS.forEach((month) => {
            if (months && months.some((m: OptionV2) => Number(m?.value) === month?.value)) {
                if (watch(`proportion[${month?.value}]`) === undefined) {
                    setValue(`proportion[${month?.value}]`, 0);
                }
            } else {
                setValue(`proportion[${month?.value}]`, 0);
            }
        })
    }, [months]);

    useEffect(() => {
        setValue("months", []);

        if (submiting) return;

        if (farmSelected) {
            monthlyEvolution.clear();
            parameters.clear();

            setValue("months", []);
            setSumProportion(0);

            // reset proportions
            Constants.MONTHS_FULL_NAME_OPTIONS.forEach((month) => {
                setValue(`proportion[${month.value}]`, null);
            });

            // reset parameters
            const parameterKeys = [
                "BIRTH_WEIGHT",
                "WEANING_WEIGHT_210_DAYS",
                "AVERAGE_DAILY_GAIN_REARING_WET",
                "AVERAGE_DAILY_GAIN_REARING_DRY",
                "ADULT_WEIGHT",
            ];

            ["MALE", "FEMALE"].forEach((type) => {
                parameterKeys.forEach((key) => {
                    setValue(`parameter.${key}.${type}`, " ");
                });
            });

            axios
                .get(`/monthly-evolution/${farmSelected?.value}`)
                .then((response) => {
                    monthlyEvolution.clear();

                    response.data.map((item: any) => monthlyEvolution.set(item.month, item.id));

                    const monthsData = response.data.sort((a: any, b: any) => a.id - b.id).map((item: any) => {
                        return monthOptions.find(month => {
                            if (item.rate_months_value == 0) return
                            return month?.value === item?.month
                        });
                    });

                    const reorderedMonthsData = (() => {
                        const lastUndefinedIndex = monthsData.findIndex((month: any) => month === undefined);
                        if (lastUndefinedIndex === -1) return monthsData;
                        const monthsAfterLastUndefined = monthsData.slice(lastUndefinedIndex + 1);
                        const monthsBeforeLastUndefined = monthsData.slice(0, lastUndefinedIndex + 1);
                        return [...monthsAfterLastUndefined, ...monthsBeforeLastUndefined];
                    })();

                    setValue("months", reorderedMonthsData.filter((month: any) => month !== undefined));

                    response.data.map((item: any) => {
                        setValue(`proportion[${item.month}]`, item.rate_months_value)
                    });
                })

            axios
                .get(`/parameter-evolution/${farmSelected?.value}`)
                .then((response) => {
                    parameters.clear();

                    response.data.map((item: any) => parameters.set(`${item.parameter}.${item.type}`, item.id))

                    const parameter = response.data.reduce((acc: any, curr: any) => {
                        if (!acc[curr.parameter]) {
                            acc[curr.parameter] = {};
                        }
                        acc[curr.parameter][curr.type] = curr?.value;
                        return acc;
                    }, {});

                    Object.keys(parameter).forEach((paramKey) => {
                        Object.keys(parameter[paramKey]).forEach((typeKey) => {
                            const fieldName = `parameter.${paramKey}.${typeKey}`;
                            const fieldValue = parameter[paramKey][typeKey];

                            setValue(fieldName, fieldValue);
                        });
                    });
                })

        }
    }, [farmSelected, submiting]);

    function saveMonthyEvolution(response: any) {
        if (submiting || !farmSelected) {
            return;
        }

        setSubmiting(true);
        const farmId = farmSelected.value;

        const mapMounths = new Map<number, any>()

        response.months.map((month: OptionV2, index: number) => {
            mapMounths.set(Number(month.value), {
                id: monthlyEvolution.get(Number(month.value)) || null,
                month: Number(month.value),
                name: Constants.MONTHS_FULL_NAME_OPTIONS.find(option => option.value === Number(month.value))?.label || month.label,
                days_per_month: month.info,
                rate_months_value: response.proportion[Number(month.value)] || 0,
                initial_month: index == 0,
                farm_id: Number(farmId)
            })
        })

        const allMounths = Constants.MONTHS_FULL_NAME_OPTIONS.map(month => mapMounths.has(month.value) ? mapMounths.get(month.value) : {
            id: monthlyEvolution.get(month.value) || null,
            month: Number(month.value),
            name: month.label,
            days_per_month: month.info,
            rate_months_value: 0,
            initial_month: false,
            farm_id: Number(farmId)
        })

        axios
            .post("/monthly-evolution", allMounths)
            .then(() => {
                const parameterEvolution = [{
                    id: parameters.get(`BIRTH_WEIGHT.MALE`) || null,
                    parameter: "BIRTH_WEIGHT",
                    type: "MALE",
                    value: response.parameter.BIRTH_WEIGHT.MALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`BIRTH_WEIGHT.FEMALE`) || null,
                    parameter: "BIRTH_WEIGHT",
                    type: "FEMALE",
                    value: response.parameter.BIRTH_WEIGHT.FEMALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`WEANING_WEIGHT_210_DAYS.MALE`) || null,
                    parameter: "WEANING_WEIGHT_210_DAYS",
                    type: "MALE",
                    value: response.parameter.WEANING_WEIGHT_210_DAYS.MALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`WEANING_WEIGHT_210_DAYS.FEMALE`) || null,
                    parameter: "WEANING_WEIGHT_210_DAYS",
                    type: "FEMALE",
                    value: response.parameter.WEANING_WEIGHT_210_DAYS.FEMALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`AVERAGE_DAILY_GAIN_REARING_WET.MALE`) || null,
                    parameter: "AVERAGE_DAILY_GAIN_REARING_WET",
                    type: "MALE",
                    value: response.parameter.AVERAGE_DAILY_GAIN_REARING_WET.MALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`AVERAGE_DAILY_GAIN_REARING_WET.FEMALE`) || null,
                    parameter: "AVERAGE_DAILY_GAIN_REARING_WET",
                    type: "FEMALE",
                    value: response.parameter.AVERAGE_DAILY_GAIN_REARING_WET.FEMALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`AVERAGE_DAILY_GAIN_REARING_DRY.MALE`) || null,
                    parameter: "AVERAGE_DAILY_GAIN_REARING_DRY",
                    type: "MALE",
                    value: response.parameter.AVERAGE_DAILY_GAIN_REARING_DRY.MALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`AVERAGE_DAILY_GAIN_REARING_DRY.FEMALE`) || null,
                    parameter: "AVERAGE_DAILY_GAIN_REARING_DRY",
                    type: "FEMALE",
                    value: response.parameter.AVERAGE_DAILY_GAIN_REARING_DRY.FEMALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`ADULT_WEIGHT.MALE`) || null,
                    parameter: "ADULT_WEIGHT",
                    type: "MALE",
                    value: response.parameter.ADULT_WEIGHT.MALE,
                    farm_id: farmId
                }, {
                    id: parameters.get(`ADULT_WEIGHT.FEMALE`) || null,
                    parameter: "ADULT_WEIGHT",
                    type: "FEMALE",
                    value: response.parameter.ADULT_WEIGHT.FEMALE,
                    farm_id: farmId
                }]

                axios.post("/parameter-evolution", parameterEvolution)
                    .then(() => {
                        axios.post(`/evolutions/${farmId}`)
                            .then(() => {
                                setSystemManager(null);
                                reset();
                            })
                            .catch(error => {
                                setErrorModal(true)
                                setErrorMessage(error.response?.data?.message || "Ocorreu um erro ao salvar os parâmetros de evolução");
                                console.error("Error saving parameter evolution:", error);
                            })
                    })
                    .catch((error) => {
                        setErrorModal(true)
                        setErrorMessage(error.response?.data?.message || "Ocorreu um erro ao salvar os parâmetros de evolução");
                        console.error("Error saving parameter evolution:", error);
                    });
            })
            .catch((error) => {
                setErrorModal(true)
                setErrorMessage(error.response?.data?.message || "Ocorreu um erro ao salvar a evolução mensal");
                console.error("Error saving monthly evolution:", error);
            })
            .finally(() => {
                setSubmiting(false);
            });
    }

    return (
        <>
            <Modal
                visible={errorModal}
                contentStyle={{
                    padding: "60px",
                    width: "433px",
                    height: "auto",
                    borderRadius: 25,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    gap: 30
                }}
            >
                <span>
                    {errorMessage || t("weightCategories.actionError")}
                </span>
                <footer className="buttons-container">
                    <ButtonOnlyTitle
                        title={t("weightCategories.back")}
                        theme="info"
                        onClick={() => {
                            setErrorModal(false)
                        }}
                    />
                </footer>
            </Modal>

            <form onSubmit={handleSubmit((data) => console.log(data))}>
                <main style={{ overflow: "auto" }} className="weightAnimalsForms">
                    <div style={{ maxWidth: 600 }}>
                        <div className={'body__formEditOnboarding'} style={{ flexDirection: "column", overflow: "auto" }}>
                            <div>
                                <Controller
                                    name="months"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiDropdownV2
                                            name="months"
                                            label={t("weightCategories.birthSeason")}
                                            placeholder={t("weightCategories.selectMonths")}
                                            options={monthSelectOptions}
                                            value={field.value ?? []}
                                            menuMount="overlay"
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            error={errors.months?.message as string | undefined}
                                            emptyMessage={t("weightCategories.noMonths")}
                                        />
                                    )}
                                />
                                <br/>

                                {months && months.map((month: OptionV2) => {
                                    return (
                                        <div
                                            key={String(month.value)}
                                            style={{
                                              display: "flex",
                                              marginBottom: 18
                                            }}
                                        >
                                            <Controller
                                                control={control}
                                                name={`proportion[${month.value}]`}
                                                render={({ field }) => (
                                                    <InputV2
                                                        name={`proportion-${month.value}`}
                                                        type="number"
                                                        label={t("weightCategories.proportion", { month: t(`weightCategories.months.${Number(month.value) - 1}`) })}
                                                        value={displayNumericFormValue(field.value)}
                                                        onChange={(v) => {
                                                            const raw = v === "" ? "" : Number(v);
                                                            if (raw === "" || Number.isNaN(raw as number)) {
                                                                field.onChange("");
                                                                return;
                                                            }
                                                            const num = raw as number;
                                                            if (num < 0) field.onChange(0);
                                                            else if (num > 100) field.onChange(100);
                                                            else field.onChange(num);
                                                        }}
                                                        onBlur={field.onBlur}
                                                        suffix="%"
                                                    />
                                                )}
                                            />
                                        </div>
                                    );
                                })}

                                <br />
                                {months && months.length > 0 && (errors?.proportion as any)?.message && sumProportion != 100 && (
                                    <small className="ov2-field__error animation__fadeIn">
                                        {formatErrorMessageV2(t("weightCategories.proportionsTotal", { total: sumProportion }))}
                                    </small>
                                )}
                            </div>


                            <div>
                                <h2>{t("weightCategories.parameters")}</h2>

                                <div className="weightAnimalsContainers">
                                    <h5 className="weightAnimalsLabel">{t("weightCategories.birthWeight")}</h5>
                                    <div className="weightAnimalsInputs" style={{ justifyContent: "space-between" }}>
                                        <div style={{ width: 300, maxWidth: "100%" }}>
                                            <Controller
                                                control={control}
                                                name="parameter.BIRTH_WEIGHT.MALE"
                                                render={({ field }) => (
                                                    <InputV2
                                                        name="parameter.BIRTH_WEIGHT.MALE"
                                                        type="number"
                                                        label={t("weightCategories.male")}
                                                        value={displayNumericFormValue(field.value)}
                                                        error={(errors?.parameter as any)?.BIRTH_WEIGHT?.MALE?.message}
                                                        onChange={(v) => {
                                                            if (v === "") field.onChange(undefined);
                                                            else {
                                                                const n = Number(v);
                                                                field.onChange(Number.isNaN(n) ? undefined : n);
                                                            }
                                                        }}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div style={{ width: 300, maxWidth: "100%" }}>
                                            <Controller
                                                control={control}
                                                name="parameter.BIRTH_WEIGHT.FEMALE"
                                                render={({ field }) => (
                                                    <InputV2
                                                        name="parameter.BIRTH_WEIGHT.FEMALE"
                                                        type="number"
                                                        label={t("weightCategories.female")}
                                                        value={displayNumericFormValue(field.value)}
                                                        error={(errors?.parameter as any)?.BIRTH_WEIGHT?.FEMALE?.message}
                                                        onChange={(v) => {
                                                            if (v === "") field.onChange(undefined);
                                                            else {
                                                                const n = Number(v);
                                                                field.onChange(Number.isNaN(n) ? undefined : n);
                                                            }
                                                        }}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="weightAnimalsContainers">
                                    <h5 className="weightAnimalsLabel">{t("weightCategories.weaningWeight")}</h5>
                                    <div className="weightAnimalsInputs" style={{ justifyContent: "space-between" }}>
                                        <div style={{ width: 300, maxWidth: "100%" }}>
                                            <Controller
                                                control={control}
                                                name="parameter.WEANING_WEIGHT_210_DAYS.MALE"
                                                render={({ field }) => (
                                                    <InputV2
                                                        name="parameter.WEANING_WEIGHT_210_DAYS.MALE"
                                                        type="number"
                                                        label={t("weightCategories.male")}
                                                        value={displayNumericFormValue(field.value)}
                                                        error={(errors?.parameter as any)?.WEANING_WEIGHT_210_DAYS?.MALE?.message}
                                                        onChange={(v) => {
                                                            if (v === "") field.onChange(undefined);
                                                            else {
                                                                const n = Number(v);
                                                                field.onChange(Number.isNaN(n) ? undefined : n);
                                                            }
                                                        }}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div style={{ width: 300, maxWidth: "100%" }}>
                                            <Controller
                                                control={control}
                                                name="parameter.WEANING_WEIGHT_210_DAYS.FEMALE"
                                                render={({ field }) => (
                                                    <InputV2
                                                        name="parameter.WEANING_WEIGHT_210_DAYS.FEMALE"
                                                        type="number"
                                                        label={t("weightCategories.female")}
                                                        value={displayNumericFormValue(field.value)}
                                                        error={(errors?.parameter as any)?.WEANING_WEIGHT_210_DAYS?.FEMALE?.message}
                                                        onChange={(v) => {
                                                            if (v === "") field.onChange(undefined);
                                                            else {
                                                                const n = Number(v);
                                                                field.onChange(Number.isNaN(n) ? undefined : n);
                                                            }
                                                        }}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="weightAnimalsContainers">
                                    <h5 className="weightAnimalsLabel">{t("weightCategories.rearingDailyGain")}</h5>

                                    <div style={{
                                        borderLeft: "3px solid #62b8ae", paddingLeft: 10, marginBottom: 10
                                    }}>
                                        <span>{t("weightCategories.wetSeason")}</span>
                                        <div className="weightAnimalsInputs" style={{ justifyContent: "space-between" }}>
                                            <div style={{ width: 300, maxWidth: "100%" }}>
                                                <Controller
                                                    control={control}
                                                    name="parameter.AVERAGE_DAILY_GAIN_REARING_WET.MALE"
                                                    render={({ field }) => (
                                                        <InputV2
                                                            name="parameter.AVERAGE_DAILY_GAIN_REARING_WET.MALE"
                                                            type="number"
                                                            label={t("weightCategories.male")}
                                                            value={displayNumericFormValue(field.value)}
                                                            error={(errors?.parameter as any)?.AVERAGE_DAILY_GAIN_REARING_WET?.MALE?.message}
                                                            onChange={(v) => {
                                                                if (v === "") field.onChange(undefined);
                                                                else {
                                                                    const n = Number(v);
                                                                    field.onChange(Number.isNaN(n) ? undefined : n);
                                                                }
                                                            }}
                                                            onBlur={field.onBlur}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div style={{ width: 300, maxWidth: "100%" }}>
                                                <Controller
                                                    control={control}
                                                    name="parameter.AVERAGE_DAILY_GAIN_REARING_WET.FEMALE"
                                                    render={({ field }) => (
                                                        <InputV2
                                                            name="parameter.AVERAGE_DAILY_GAIN_REARING_WET.FEMALE"
                                                            type="number"
                                                            label={t("weightCategories.female")}
                                                            value={displayNumericFormValue(field.value)}
                                                            error={(errors?.parameter as any)?.AVERAGE_DAILY_GAIN_REARING_WET?.FEMALE?.message}
                                                            onChange={(v) => {
                                                                if (v === "") field.onChange(undefined);
                                                                else {
                                                                    const n = Number(v);
                                                                    field.onChange(Number.isNaN(n) ? undefined : n);
                                                                }
                                                            }}
                                                            onBlur={field.onBlur}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        borderLeft: "3px solid #d9b43c", paddingLeft: 10, marginBottom: 10
                                    }}>
                                        <span>{t("weightCategories.drySeason")}</span>
                                        <div className="weightAnimalsInputs" style={{ justifyContent: "space-between" }}>
                                            <div style={{ width: 300, maxWidth: "100%" }}>
                                                <Controller
                                                    control={control}
                                                    name="parameter.AVERAGE_DAILY_GAIN_REARING_DRY.MALE"
                                                    render={({ field }) => (
                                                        <InputV2
                                                            name="parameter.AVERAGE_DAILY_GAIN_REARING_DRY.MALE"
                                                            type="number"
                                                            label={t("weightCategories.male")}
                                                            value={displayNumericFormValue(field.value)}
                                                            error={(errors?.parameter as any)?.AVERAGE_DAILY_GAIN_REARING_DRY?.MALE?.message}
                                                            onChange={(v) => {
                                                                if (v === "") field.onChange(undefined);
                                                                else {
                                                                    const n = Number(v);
                                                                    field.onChange(Number.isNaN(n) ? undefined : n);
                                                                }
                                                            }}
                                                            onBlur={field.onBlur}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div style={{ width: 300, maxWidth: "100%" }}>
                                                <Controller
                                                    control={control}
                                                    name="parameter.AVERAGE_DAILY_GAIN_REARING_DRY.FEMALE"
                                                    render={({ field }) => (
                                                        <InputV2
                                                            name="parameter.AVERAGE_DAILY_GAIN_REARING_DRY.FEMALE"
                                                            type="number"
                                                            label={t("weightCategories.female")}
                                                            value={displayNumericFormValue(field.value)}
                                                            error={(errors?.parameter as any)?.AVERAGE_DAILY_GAIN_REARING_DRY?.FEMALE?.message}
                                                            onChange={(v) => {
                                                                if (v === "") field.onChange(undefined);
                                                                else {
                                                                    const n = Number(v);
                                                                    field.onChange(Number.isNaN(n) ? undefined : n);
                                                                }
                                                            }}
                                                            onBlur={field.onBlur}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="weightAnimalsContainers">
                                    <h5 className="weightAnimalsLabel">{t("weightCategories.adultWeight")}</h5>
                                    <div className="weightAnimalsInputs" style={{ justifyContent: "space-between" }}>
                                        <div style={{ width: 300, maxWidth: "100%" }}>
                                            <Controller
                                                control={control}
                                                name="parameter.ADULT_WEIGHT.MALE"
                                                render={({ field }) => (
                                                    <InputV2
                                                        name="parameter.ADULT_WEIGHT.MALE"
                                                        type="number"
                                                        label={t("weightCategories.male")}
                                                        value={displayNumericFormValue(field.value)}
                                                        error={(errors?.parameter as any)?.ADULT_WEIGHT?.MALE?.message}
                                                        onChange={(v) => {
                                                            if (v === "") field.onChange(undefined);
                                                            else {
                                                                const n = Number(v);
                                                                field.onChange(Number.isNaN(n) ? undefined : n);
                                                            }
                                                        }}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div style={{ width: 300, maxWidth: "100%" }}>
                                            <Controller
                                                control={control}
                                                name="parameter.ADULT_WEIGHT.FEMALE"
                                                render={({ field }) => (
                                                    <InputV2
                                                        name="parameter.ADULT_WEIGHT.FEMALE"
                                                        type="number"
                                                        label={t("weightCategories.female")}
                                                        value={displayNumericFormValue(field.value)}
                                                        error={(errors?.parameter as any)?.ADULT_WEIGHT?.FEMALE?.message}
                                                        onChange={(v) => {
                                                            if (v === "") field.onChange(undefined);
                                                            else {
                                                                const n = Number(v);
                                                                field.onChange(Number.isNaN(n) ? undefined : n);
                                                            }
                                                        }}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <br />
                            </div>
                        </div>
                    </div>

                </main>

                <div className="delimiter" style={{ marginTop: 15 }} />

                <FooterEditOnboarding
                    undoFunction={() => setSystemManager(null)}
                    saveFunction={handleSubmit((data) => {
                        if (!submiting) {
                            saveMonthyEvolution(data)
                        }
                    })}
                    isNew={false}
                    disabledSubmit={submiting}
                />
            </form>
        </>
    )
}
